import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';

interface ContactData {
  id?: number | string;
  name: string;
  surname: string;
  email: string;
  phone_number?: string;
  lead_source?: string;
  job_title?: string;
  linkedin?: string;
  personal_background?: string;
  company_id?: number;
}

@Component({
  selector: 'app-add-contact-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './add-contact-modal.component.html',
  styleUrl: './add-contact-modal.component.scss'
})
export class AddContactModalComponent implements OnInit, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() companies: any[] = [];
  @Input() contact: any = null; // For edit mode
  @Input() mode: 'create' | 'edit' = 'create';
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<ContactData>();
  @Output() deleteCompanyContact = new EventEmitter<number>();

  contactForm: FormGroup;
  loading: boolean = false;
  leadSources = ['SalesOMMO', 'Website', 'LinkedIn', 'Referral', 'Conference', 'Partner', 'Webinar', 'Other'];
  existingCompanies: any[] = [];

  constructor(private fb: FormBuilder) {
    this.contactForm = this.createForm();
  }

  ngOnInit(): void {
    // If in edit mode, populate the form
    if (this.mode === 'edit' && this.contact) {
      this.populateForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to contact, mode, or isVisible inputs
    if (changes['contact'] || changes['mode'] || changes['isVisible']) {
      // If modal becomes visible and we're in edit mode, populate the form
      if (this.isVisible && this.mode === 'edit' && this.contact) {
        this.populateForm();
      } else if (this.isVisible && this.mode === 'create') {
        this.contactForm.reset();
        this.contactForm.patchValue({
          lead_source: 'SalesOMMO'
        });
        this.existingCompanies = [];
      }
    }
  }

  private createForm(): FormGroup {
    // Stricter email validation pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Phone number validation pattern - accepts international formats
    // Allows: +, digits, spaces, parentheses, and dashes
    // Examples: +1 (555) 123-4567, +44 20 7946 0958, 555-123-4567, (555) 123-4567
    const phonePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;

    return this.fb.group({
      name: ['', [Validators.required]],
      surname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.pattern(emailPattern)]],
      phone_number: ['', [Validators.pattern(phonePattern)]],
      lead_source: ['SalesOMMO'],
      job_title: [''],
      linkedin: ['', [Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/.*/)]],
      personal_background: [''],
      company_id: ['']
    });
  }


  private populateForm(): void {
    if (this.contact) {
      // Store existing companies for display
      this.existingCompanies = this.contact.company_contacts || [];

      // In edit mode, don't pre-select a company since we show them separately
      this.contactForm.patchValue({
        name: this.contact.name || '',
        surname: this.contact.surname || '',
        email: this.contact.email || '',
        phone_number: this.contact.phone_number || '',
        lead_source: this.contact.lead_source || '',
        job_title: this.contact.job_title || '',
        linkedin: this.contact.linkedin || '',
        personal_background: this.contact.personal_background || '',
        company_id: '' // Empty in edit mode as we show existing companies separately
      });
    }
  }

  removeCompany(companyContactId: number): void {
    if (confirm('Are you sure you want to remove this company association?')) {
      this.deleteCompanyContact.emit(companyContactId);
      // Remove from local list for immediate UI update
      this.existingCompanies = this.existingCompanies.filter(cc => cc.id !== companyContactId);
    }
  }

  closeModal(): void {
    this.resetForm();
    this.close.emit();
  }

  saveContact(): void {
    if (this.contactForm.valid) {
      this.loading = true;

      const formValue = this.contactForm.value;
      const contactData: ContactData = {
        name: formValue.name,
        surname: formValue.surname,
        email: formValue.email,
        phone_number: formValue.phone_number || undefined,
        lead_source: formValue.lead_source || undefined,
        job_title: formValue.job_title || undefined,
        linkedin: formValue.linkedin || undefined,
        personal_background: formValue.personal_background || undefined,
        company_id: formValue.company_id || undefined
      };

      // If in edit mode, include the contact ID
      if (this.mode === 'edit' && this.contact) {
        contactData.id = this.contact.id;
      }

      // Emit the data immediately - the parent component will handle the API call
      this.save.emit(contactData);
      this.loading = false;
      this.closeModal();
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.contactForm.controls).forEach(key => {
        this.contactForm.controls[key].markAsTouched();
      });
    }
  }

  private resetForm(): void {
    this.contactForm.reset();
    this.contactForm.patchValue({
      lead_source: 'SalesOMMO'
    });
    this.loading = false;
    this.existingCompanies = [];
  }
}
